import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getStandupHistory,
  getStandupBoard,
  type StandupHistoryEntry,
  type StandupBoard,
  type BoardMember,
  type TicketDiff,
  type Focus,
} from "../../lib/api";
import { getStandupTranscript } from "../../lib/brain-api";
import { Spinner } from "../../design/ui";
import { StandupStyles, avatarTint, initials, firstName } from "./StandupPage";

// Standup history — read-only drill-in over closed standup days. Reuses the
// "Synaptic" scoped styles (SN_CSS) + avatar helpers from StandupPage; adds a
// small history-only stylesheet for the list rows, ticket-diff and transcript.
// No edit affordances exist here: a historical day is never mutable from the UI.

// ---------------------------------------------------------------------------
// History-only scoped styles (page-local, same token vocabulary as SN_CSS).
// ---------------------------------------------------------------------------
const SH_CSS = `
.sh-back{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:#0a6e72;font-size:13.5px;font-weight:500;cursor:pointer;padding:0;font-family:inherit;margin-bottom:14px;}
.sh-back:hover{text-decoration:underline;}
.sh-title{font-size:24px;font-weight:700;color:#15252b;letter-spacing:-0.01em;}
.sh-sub{font-size:13.5px;color:#6c777c;margin-top:6px;}

/* list */
.sh-list{display:flex;flex-direction:column;gap:10px;margin-top:24px;}
.sh-row{display:flex;align-items:center;gap:16px;padding:16px 18px;border-radius:9px;background:#fff;border:1px solid #e7e9ea;cursor:pointer;text-align:left;width:100%;font-family:inherit;transition:all .12s ease;}
.sh-row:hover{border-color:var(--color-primary);box-shadow:0 1px 4px rgba(0,0,0,.06);}
.sh-row-date{font-family:var(--font-mono);font-size:14px;font-weight:600;color:#15252b;flex:none;min-width:122px;}
.sh-row-digest{font-size:13.5px;color:#56656b;line-height:1.5;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;}
.sh-row-digest.muted{color:#a4b0b5;font-style:italic;}
.sh-row-chev{color:#b8c2c7;font-size:16px;flex:none;}

/* per-day */
.sh-day-band{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;padding-bottom:20px;border-bottom:1px solid #e7e9ea;margin-bottom:22px;}
.sh-roclock{font-family:var(--font-mono);font-size:34px;font-weight:500;color:#15252b;letter-spacing:-0.01em;line-height:1;}
.sh-rodate{font-size:13.5px;color:#6c777c;margin-top:8px;}

/* digest + transcript blocks */
.sh-prose{font-size:14px;line-height:1.6;color:#37474f;white-space:pre-wrap;}
.sh-prose.muted{color:#a4b0b5;font-style:italic;}

/* ticket diff */
.sh-diff{display:flex;flex-direction:column;gap:8px;}
.sh-diffrow{display:flex;align-items:center;gap:11px;padding:10px 13px;border-radius:7px;background:#fff;border:1px solid #ebedee;}
.sh-diffrow.done{background:#fafbfb;border-color:#eef0f1;}
.sh-diffrow.blocked{background:#fdeced;border-color:#f3d2d5;}
.sh-diffrow.moved{background:#fbf2e0;border-color:#f0e2c2;}
.sh-diffkey{font-family:var(--font-mono);font-size:12px;padding:2px 8px;border-radius:4px;flex:none;background:#eef1f2;color:#5a6b72;}
.sh-diffsum{font-size:14px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#37474f;}
.sh-difftag{font-family:var(--font-mono);font-size:10.5px;letter-spacing:.04em;text-transform:uppercase;padding:3px 9px;border-radius:11px;flex:none;}
.sh-difftag.done{background:#eaf2ee;color:#4c8068;}
.sh-difftag.blocked{background:#f7d6d8;color:#a13a40;}
.sh-difftag.status{background:#eef1f2;color:#5a6b72;}
.sh-diffstatus{font-family:var(--font-mono);font-size:11px;color:#90a0a6;flex:none;}

/* transcript dialogue */
.sh-dlg{display:flex;flex-direction:column;gap:14px;}
.sh-turn{display:grid;grid-template-columns:32px 1fr;gap:10px;align-items:start;}
.sh-turn-av{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;flex:none;letter-spacing:.02em;}
.sh-turn-body{min-width:0;padding-top:2px;}
.sh-turn-who{font-size:12.5px;font-weight:600;color:#15252b;margin-bottom:3px;}
.sh-turn-text{font-size:14px;line-height:1.55;color:#37474f;white-space:pre-wrap;}
.sh-dlg-raw{font-size:13px;line-height:1.55;color:#56656b;white-space:pre-wrap;font-family:var(--font-mono);}
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtLongDate(iso: string): string {
  // iso = YYYY-MM-DD — render without timezone drift.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

/** One spoken turn after Meet resource paths are collapsed into readable labels. */
export interface TranscriptTurn {
  speakerId: string;
  label: string; // "Speaker A", …
  text: string;
}

const PARTICIPANT_RE = /^(?:conferenceRecords\/[^:\n]+\/)?participants\/(\d+)\s*:\s*(.*)$/;
const GENERIC_SPEAKER_RE = /^([^:\n]{1,200}):\s*(.+)$/;

/**
 * Parse a raw Meet transcript into dialogue turns. Strips
 * `conferenceRecords/.../participants/{id}:` prefixes (legacy ingest), uses real
 * display names when already stored as "Name: text", assigns Speakers A/B/… only
 * for unresolved participant IDs, and merges consecutive same-speaker turns.
 */
export function parseTranscriptDialogue(raw: string): TranscriptTurn[] {
  const lines = raw.split(/\r?\n/);
  const labelById = new Map<string, string>();
  const turns: TranscriptTurn[] = [];

  const syntheticLabel = (id: string): string => {
    let label = labelById.get(id);
    if (!label) {
      const n = labelById.size;
      label = `Speaker ${String.fromCharCode(65 + (n % 26))}${n >= 26 ? String(Math.floor(n / 26)) : ""}`;
      labelById.set(id, label);
    }
    return label;
  };

  const push = (speakerId: string, text: string, label?: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const last = turns[turns.length - 1];
    if (last && last.speakerId === speakerId) {
      last.text = `${last.text} ${trimmed}`.replace(/\s+/g, " ").trim();
      return;
    }
    turns.push({
      speakerId,
      label: label ?? syntheticLabel(speakerId),
      text: trimmed,
    });
  };

  for (const line of lines) {
    const s = line.trim();
    if (!s) continue;
    const m = PARTICIPANT_RE.exec(s);
    if (m) {
      push(m[1], m[2]); // unresolved id → Speaker A/B
      continue;
    }
    // Named lines from ingest ("Jay Smith: …") or export.
    const g = GENERIC_SPEAKER_RE.exec(s);
    if (g && !s.startsWith("http") && g[1].length < 80) {
      push(g[1], g[2], g[1]);
      continue;
    }
    push("_narrator", s, "Narrator");
  }
  return turns;
}

function formatTranscriptForExport(raw: string): string {
  const turns = parseTranscriptDialogue(raw);
  if (!turns.length) return raw;
  return turns.map((t) => `${t.label}: ${t.text}`).join("\n\n");
}

function downloadTranscript(date: string, text: string) {
  const blob = new Blob([formatTranscriptForExport(text)], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `standup-transcript-${date}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function TranscriptDialogue({ raw }: { raw: string }) {
  const turns = useMemo(() => parseTranscriptDialogue(raw), [raw]);
  if (!turns.length) {
    return <div className="sh-dlg-raw">{raw}</div>;
  }
  // If almost nothing looked like speakers, fall back to raw.
  const named = turns.filter((t) => t.speakerId !== "_narrator").length;
  if (named === 0) {
    return <div className="sh-dlg-raw">{raw}</div>;
  }
  return (
    <div className="sh-dlg">
      {turns.map((t, i) => {
        const tint = avatarTint(t.speakerId);
        const letter = initials(t.label);
        return (
          <div key={`${t.speakerId}-${i}`} className="sh-turn">
            <span className="sh-turn-av" style={{ background: tint.bg, color: tint.fg }} aria-hidden="true">
              {letter}
            </span>
            <div className="sh-turn-body">
              <div className="sh-turn-who">{t.label}</div>
              <div className="sh-turn-text">{t.text}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// History list
// ---------------------------------------------------------------------------

function HistoryList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<StandupHistoryEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    getStandupHistory(ctrl.signal)
      .then(setRows)
      .catch((e) => { if (!ctrl.signal.aborted) setErr(e instanceof Error ? e.message : "Failed to load history."); });
    return () => ctrl.abort();
  }, []);

  return (
    <div className="sn-page">
      <StandupStyles />
      <style>{SH_CSS}</style>
      <button className="sh-back" onClick={() => navigate("/standup")}>← Back to today’s standup</button>
      <div className="sh-title">Standup history</div>
      <div className="sh-sub">Past standups, newest first. Open a day to review the board, recaps and ticket movement.</div>

      {!rows && !err && <Spinner />}
      {err && <div style={{ color: "#a13a40", fontSize: 14, marginTop: 18 }}>{err}</div>}
      {rows && rows.length === 0 && <div className="sn-empty" style={{ marginTop: 24 }}>No past standups yet.</div>}
      {rows && rows.length > 0 && (
        <div className="sh-list">
          {rows.map((r) => (
            <button key={r.date} className="sh-row" onClick={() => navigate(`/standup/history/${r.date}`)}>
              <span className="sh-row-date">{r.date}</span>
              <span className={`sh-row-digest${r.teamDigest ? "" : " muted"}`}>
                {r.teamDigest || "No team digest captured."}
              </span>
              <span className="sh-row-chev">›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-day read-only person panel
// ---------------------------------------------------------------------------

function YesterdayList({ focuses, recap }: { focuses: Focus[]; recap?: string }) {
  if (!recap && focuses.length === 0) {
    return <div className="sn-ytext" style={{ padding: "7px 4px" }}>Nothing logged.</div>;
  }
  return (
    <>
      {recap && <div className="sn-recap">{recap}</div>}
      <div className="sn-yest">
        {focuses.map((f) => (
          <div key={f.id} className="sn-yrow">
            <div className={`sn-ybox${f.done ? " done" : ""}`}>{f.done && <span>✓</span>}</div>
            <span className={`sn-ytext${f.done ? " done" : ""}`}>{f.text}</span>
            {f.ticketKey && <span className="sn-ykey">{f.ticketKey}</span>}
          </div>
        ))}
      </div>
    </>
  );
}

function TicketDiffList({ diff }: { diff: TicketDiff[] }) {
  if (diff.length === 0) return <div className="sn-banner-sub">No ticket movement recorded.</div>;
  return (
    <div className="sh-diff">
      {diff.map((d) => {
        const rowCls = d.movedToDone ? "done" : d.newlyBlocked ? "blocked" : d.statusChanged ? "moved" : "";
        return (
          <div key={d.key} className={`sh-diffrow ${rowCls}`}>
            <span className="sh-diffkey">{d.key}</span>
            <span className="sh-diffsum">{d.summary}</span>
            {d.movedToDone && <span className="sh-difftag done">Moved to done</span>}
            {d.newlyBlocked && <span className="sh-difftag blocked">Newly blocked</span>}
            {d.statusChanged && !d.movedToDone && !d.newlyBlocked && (
              <>
                <span className="sh-difftag status">Status change</span>
                {(d.fromStatus || d.toStatus) && (
                  <span className="sh-diffstatus">{d.fromStatus ?? "—"} → {d.toStatus ?? "—"}</span>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Read-only per-person board for a historical day — focuses, yesterday/recap,
 *  and the captured ticket diff. All edit controls are intentionally absent. */
function HistoricalPersonTab({ member }: { member: BoardMember }) {
  const focuses = member.focuses ?? [];
  const doneCount = focuses.filter((f) => f.done).length;
  const y = member.yesterday;
  const diff = member.ticketDiff ?? [];

  return (
    <div>
      <div className="sn-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        {/* THAT DAY'S FOCUSES (read-only) */}
        <div className="sn-card" style={{ padding: "20px 23px" }}>
          <div className="sn-card-hd">
            <div>
              <div className="sn-eyebrow">That day</div>
              <div className="sn-card-title">Focuses</div>
            </div>
            <span className="sn-progress">{doneCount}/{focuses.length}</span>
          </div>
          <div className="sn-focuses">
            {focuses.map((f) => (
              <div key={f.id} className="sn-focus">
                <div className={`sn-check${f.done ? " done" : ""} disabled`} role="checkbox" aria-checked={f.done} aria-disabled="true">
                  {f.done && <span>✓</span>}
                </div>
                <span className={`sn-focus-text${f.done ? " done" : ""}`}>{f.text}</span>
                {f.ticketKey && <span className="sn-keypill">{f.ticketKey}</span>}
              </div>
            ))}
            {focuses.length === 0 && <div className="sn-ytext" style={{ padding: "7px 4px" }}>No focuses logged.</div>}
          </div>
        </div>

        {/* RECAP / PRIOR DAY */}
        <div className="sn-card quiet" style={{ padding: "20px 23px" }}>
          <div className="sn-eyebrow quiet">Recap</div>
          <div className="sn-card-title quiet" style={{ marginBottom: 14 }}>End-of-day summary</div>
          <YesterdayList focuses={y?.focuses ?? []} recap={member.recap ?? y?.recap} />
        </div>
      </div>

      {/* TICKET DIFF */}
      <div className="sn-card" style={{ marginTop: 18, padding: "20px 23px" }}>
        <div className="sn-card-hd">
          <div className="sn-card-title" style={{ marginTop: 0 }}>Ticket movement</div>
          <span className="sn-eyebrow">JIRA</span>
        </div>
        <TicketDiffList diff={diff} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-day view
// ---------------------------------------------------------------------------

function HistoryDay({ date }: { date: string }) {
  const navigate = useNavigate();
  const [board, setBoard] = useState<StandupBoard | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<string>("");

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true); setErr(null);
    getStandupBoard(date, signal)
      .then((b) => {
        setBoard(b);
        const first = (b.members ?? []).filter((m) => m.kind !== "brain")[0];
        setActive(first?.userId ?? "");
      })
      .catch((e) => { if (!signal?.aborted) setErr(e instanceof Error ? e.message : "Failed to load standup."); })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
    // Transcript is best-effort; never blocks the board.
    getStandupTranscript(date)
      .then((r) => setTranscript(r?.transcript ?? null))
      .catch(() => setTranscript(null));
  }, [date]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const people = useMemo(() => (board?.members ?? []).filter((m) => m.kind !== "brain"), [board]);
  const activeMember = people.find((m) => m.userId === active);

  return (
    <div className="sn-page">
      <StandupStyles />
      <style>{SH_CSS}</style>
      <button className="sh-back" onClick={() => navigate("/standup/history")}>← Back to history</button>

      <div className="sh-day-band">
        <div>
          <div className="sh-roclock">{date}</div>
          <div className="sh-rodate">{fmtLongDate(date)}</div>
        </div>
        <div className="sn-chip closed">
          <div className="sn-chip-dot" />
          <span className="sn-chip-label">Closed — read-only</span>
        </div>
      </div>

      {loading && <Spinner />}
      {err && !loading && <div style={{ color: "#a13a40", fontSize: 14 }}>{err}</div>}

      {!loading && !err && board && (
        <>
          {/* TEAM DIGEST */}
          <div className="sn-card" style={{ padding: "20px 23px", marginBottom: 18 }}>
            <div className="sn-eyebrow">Team digest</div>
            <div className="sn-card-title" style={{ marginBottom: 14 }}>What the team did</div>
            <div className={`sh-prose${board.teamDigest ? "" : " muted"}`}>
              {board.teamDigest || "No team digest captured for this day."}
            </div>
          </div>

          {/* TRANSCRIPT */}
          <div className="sn-card quiet" style={{ padding: "20px 23px", marginBottom: 18 }}>
            <div className="sn-card-hd">
              <div>
                <div className="sn-eyebrow quiet">Transcript</div>
                <div className="sn-card-title quiet" style={{ marginBottom: 0 }}>Standup discussion</div>
              </div>
              {transcript && (
                <button
                  type="button"
                  className="sn-chip closed"
                  onClick={() => downloadTranscript(date, transcript)}
                  style={{ cursor: "pointer", border: "none", fontFamily: "inherit" }}
                >
                  <span className="sn-chip-label">Export .txt</span>
                </button>
              )}
            </div>
            <div className={transcript ? "" : "sh-prose muted"} style={{ marginTop: 14, maxHeight: 520, overflow: "auto" }}>
              {transcript ? <TranscriptDialogue raw={transcript} /> : "No transcript available for this day."}
            </div>
          </div>

          {/* PER-PERSON READ-ONLY BOARD */}
          {people.length === 0 ? (
            <div className="sn-empty">No one was on the board this day.</div>
          ) : (
            <>
              <div className="sn-tabwrap">
                <div className="sn-tabstrip" role="tablist" aria-label="People">
                  {people.map((m) => {
                    const sel = active === m.userId;
                    const tint = avatarTint(m.userId);
                    return (
                      <button key={m.userId} type="button" role="tab" aria-selected={sel} aria-label={m.displayName}
                        data-tip={sel ? undefined : firstName(m.displayName)}
                        className={`sn-tab person${sel ? " active" : ""}`} onClick={() => setActive(m.userId)}>
                        <span className="sn-tab-av" style={{ background: tint.bg, color: tint.fg }}>{initials(m.displayName)}</span>
                        <span className="sn-tab-name">{firstName(m.displayName)}</span>
                      </button>
                    );
                  })}
                  <span className="sn-tabspacer" aria-hidden="true" />
                </div>
              </div>
              {activeMember && <HistoricalPersonTab member={activeMember} />}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route entry — list vs day, switched on the :date param.
// ---------------------------------------------------------------------------

export function StandupHistoryPage() {
  const { date } = useParams<{ date?: string }>();
  return date ? <HistoryDay date={date} /> : <HistoryList />;
}
